import React, {useMemo, useEffect, useRef} from 'react'
import PropTypes from 'prop-types'

import {
    Box,
    Flex,
    Link,
    List,
    ListItem,
    useTheme,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'

import {useAccessToken} from '@salesforce/commerce-sdk-react'
import useCustomFetch from '../../hooks/useFetchContent'
import {pxToRem} from '../../utills/pxToRem'
import {categoryUrlBuilder} from '../../utills/url'
import {parseCategoryColorConfig} from '../../utills/parseCategoryColorConfig'
import {fonts} from '../../theme/foundations/fonts'
import {megaMenuContentSlotStyles} from './partials/index.style'
import {
    MEGA_MENU_HEADING_HEIGHT_PX,
    MEGA_MENU_SUBCATEGORY_HEIGHT_PX,
    MEGA_MENU_GROUP_GAP_PX,
    MEGA_MENU_MAX_COLUMN_HEIGHT_PX,
    MEGA_MENU_MAX_ALLOWED_COLUMN_HEIGHT_PX,
    MEGA_MENU_CONTENT_SLOT_ID,
    MEGA_MENU_MAXIMUM_NUMBER_COLUMNS,
    MENU_DEFAULT_TEXT_COLOR,
    MENU_DEFAULT_HOVER_COLOR
} from '../../constants'

const getCategoryHeight = (cat, itemsKey) => {
    const subCount = cat?.[itemsKey]?.length || 0
    return MEGA_MENU_HEADING_HEIGHT_PX + subCount * MEGA_MENU_SUBCATEGORY_HEIGHT_PX
}

/**
 * Greedy fill: places categories into columns left-to-right,
 * moving to the next column when adding a group would exceed targetHeight.
 * Returns { columns, maxHeight } where maxHeight is the tallest column.
 */
const greedyFill = (categories, catHeights, targetHeight, maxCols) => {
    const columns = []
    let currentColumn = []
    let currentHeight = 0
    let maxHeight = 0

    for (let i = 0; i < categories.length; i++) {
        const h = catHeights[i]

        if (columns.length >= maxCols - 1) {
            currentHeight =
                currentColumn.length === 0 ? h : currentHeight + MEGA_MENU_GROUP_GAP_PX + h
            currentColumn.push(categories[i])
            continue
        }

        if (currentColumn.length === 0) {
            currentColumn.push(categories[i])
            currentHeight = h
            continue
        }

        if (currentHeight + MEGA_MENU_GROUP_GAP_PX + h > targetHeight) {
            maxHeight = Math.max(maxHeight, currentHeight)
            columns.push(currentColumn)
            currentColumn = [categories[i]]
            currentHeight = h
        } else {
            currentHeight += MEGA_MENU_GROUP_GAP_PX + h
            currentColumn.push(categories[i])
        }
    }

    if (currentColumn.length > 0) {
        maxHeight = Math.max(maxHeight, currentHeight)
        columns.push(currentColumn)
    }

    return {columns, maxHeight}
}

const distributeColumns = (categories, maxColumns, itemsKey = 'categories') => {
    if (categories.length === 0) return {columns: [], needsScroll: false}

    const catHeights = categories.map((cat) => getCategoryHeight(cat, itemsKey))

    if (categories.length < maxColumns) {
        const result = greedyFill(
            categories,
            catHeights,
            MEGA_MENU_MAX_COLUMN_HEIGHT_PX,
            maxColumns
        )
        return {columns: result.columns, needsScroll: false}
    }

    const totalHeight =
        catHeights.reduce((a, b) => a + b, 0) + (categories.length - 1) * MEGA_MENU_GROUP_GAP_PX

    let lo = 0
    let hi = totalHeight
    let bestTarget = 0

    while (lo <= hi) {
        const mid = Math.floor((lo + hi) / 2)
        const attempt = greedyFill(categories, catHeights, mid, maxColumns)
        if (attempt.columns.length >= maxColumns) {
            bestTarget = mid
            lo = mid + 1
        } else {
            hi = mid - 1
        }
    }

    const result = greedyFill(categories, catHeights, bestTarget, maxColumns)

    if (result.maxHeight <= MEGA_MENU_MAX_COLUMN_HEIGHT_PX) {
        return {columns: result.columns, needsScroll: false}
    }

    if (result.maxHeight <= MEGA_MENU_MAX_ALLOWED_COLUMN_HEIGHT_PX) {
        return {columns: result.columns, needsScroll: false}
    }

    return {columns: result.columns, needsScroll: true}
}

const MegaMenuContent = ({
    maxColumns = MEGA_MENU_MAXIMUM_NUMBER_COLUMNS,
    item,
    itemsKey = 'categories',
    onClose,
    initialFocusRef
}) => {
    const theme = useTheme()
    const {baseStyle} = theme.components.MegaMenu
    const {token} = useAccessToken()
    const items = item?.[itemsKey] || []
    const parentCategoryName = item?.name?.toLowerCase()

    const slotId = `${MEGA_MENU_CONTENT_SLOT_ID}${encodeURIComponent(parentCategoryName)}`
    const {response: slotResponse} = useCustomFetch(token, slotId)

    const getContentInfo = (html) => {
        if (!html) return {hasContent: false, contentCount: 0}
        const stripped = html.replace(/<!--[\s\S]*?-->/g, '').trim()
        if (!stripped) return {hasContent: false, contentCount: 0}
        const liMatches = stripped.match(/<li\b[^>]*>/gi)
        const count = liMatches ? liMatches.length : 0
        return {hasContent: count > 0, contentCount: count}
    }

    const {hasContent, contentCount} = getContentInfo(slotResponse)

    const responsiveMaxColumns = useBreakpointValue({
        base: 3,
        sm: 3,
        md: hasContent ? 3 : 4,
        lg: 4,
        xl: 4
    })
    const effectiveMaxColumns = Math.min(maxColumns, responsiveMaxColumns ?? maxColumns)

    const contentRef = useRef(null)

    useEffect(() => {
        if (!hasContent || !contentRef.current) return
        const el = contentRef.current

        const allImages = el.querySelectorAll('img')
        allImages.forEach((img) => {
            img.removeAttribute('width')
            img.removeAttribute('height')
        })

        const carousel = el.querySelector('.shop-by-brand-carousel')
        if (!carousel) return

        const liItems = carousel.querySelectorAll(':scope > li')
        if (liItems.length <= 1) {
            carousel.style.justifyContent = 'flex-end'
            el.style.paddingRight = pxToRem(45)
            return
        }

        carousel.style.justifyContent = 'flex-start'
        el.style.transition = 'padding-left 0.3s ease'
        let rightArrowBtn = null
        let leftArrowBtn = null

        // Drag scrolling (mouse + touch)
        let isDragging = false
        let dragStartX = 0
        let dragScrollLeft = 0

        const onDragStart = (x) => {
            isDragging = true
            carousel.style.cursor = 'grabbing'
            dragStartX = x
            dragScrollLeft = carousel.scrollLeft
        }
        const onDragEnd = () => {
            isDragging = false
            carousel.style.cursor = 'grab'
        }
        const onDragMove = (x, e) => {
            if (!isDragging) return
            if (e) e.preventDefault()
            carousel.scrollLeft = dragScrollLeft - (x - dragStartX)
        }

        const onMouseDown = (e) => onDragStart(e.pageX)
        const onMouseLeave = () => {
            if (isDragging) onDragEnd()
        }
        const onMouseUp = () => onDragEnd()
        const onMouseMove = (e) => onDragMove(e.pageX, e)

        const onTouchStart = (e) => onDragStart(e.touches[0].pageX)
        const onTouchEnd = () => onDragEnd()
        const onTouchMove = (e) => onDragMove(e.touches[0].pageX, e)

        const checkOverflow = () => {
            const gap = parseFloat(getComputedStyle(carousel).columnGap) || 16
            let totalWidth = 0
            const currentLiItems = carousel.querySelectorAll(':scope > li')
            currentLiItems.forEach((item, i) => {
                totalWidth += item.getBoundingClientRect().width
                if (i < currentLiItems.length - 1) totalWidth += gap
            })
            return totalWidth > carousel.getBoundingClientRect().width
        }

        const getScrollAmount = () => {
            const firstItem = carousel.querySelector(':scope > li')
            if (!firstItem) return 200
            const gap = parseFloat(getComputedStyle(carousel).columnGap) || 16
            return firstItem.getBoundingClientRect().width + gap
        }

        const handleRightClick = () => {
            carousel.scrollBy({left: getScrollAmount(), behavior: 'smooth'})
        }

        const handleLeftClick = () => {
            carousel.scrollBy({left: -getScrollAmount(), behavior: 'smooth'})
        }

        const updateArrows = () => {
            const maxScroll = carousel.scrollWidth - carousel.clientWidth
            const atEnd = carousel.scrollLeft >= maxScroll - 1
            if (leftArrowBtn) {
                leftArrowBtn.style.display = carousel.scrollLeft > 1 ? 'flex' : 'none'
            }
            if (rightArrowBtn) {
                rightArrowBtn.style.display = !atEnd ? 'flex' : 'none'
            }
            el.style.paddingLeft = atEnd ? '0' : ''
        }

        const arrowBaseStyle = `
            align-items: center;
            justify-content: center;
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            background: none;
            border: none;
            width: ${pxToRem(40)};
            height: ${pxToRem(40)};
            cursor: pointer;
            padding: 0;
            z-index: 10;
        `

        const createArrows = () => {
            el.style.position = 'relative'

            rightArrowBtn = document.createElement('button')
            rightArrowBtn.setAttribute('aria-label', 'Next')
            rightArrowBtn.innerHTML = `<svg width="40" height="40" viewBox="0 0 40 40"><use href="#right-arrow" /></svg>`
            rightArrowBtn.style.cssText = `display: flex; right: 0; ${arrowBaseStyle}`
            rightArrowBtn.addEventListener('mouseenter', () => {
                rightArrowBtn.querySelector('use').setAttribute('href', '#right-arrow-hover')
            })
            rightArrowBtn.addEventListener('mouseleave', () => {
                rightArrowBtn.querySelector('use').setAttribute('href', '#right-arrow')
            })
            rightArrowBtn.addEventListener('click', handleRightClick)
            el.appendChild(rightArrowBtn)

            leftArrowBtn = document.createElement('button')
            leftArrowBtn.setAttribute('aria-label', 'Previous')
            leftArrowBtn.innerHTML = `<svg width="40" height="40" viewBox="0 0 40 40"><use href="#right-arrow" /></svg>`
            leftArrowBtn.style.cssText = `${arrowBaseStyle} display: none; left: 0; transform: scaleX(-1) translateY(-50%);`
            leftArrowBtn.addEventListener('mouseenter', () => {
                leftArrowBtn.querySelector('use').setAttribute('href', '#right-arrow-hover')
            })
            leftArrowBtn.addEventListener('mouseleave', () => {
                leftArrowBtn.querySelector('use').setAttribute('href', '#right-arrow')
            })
            leftArrowBtn.addEventListener('click', handleLeftClick)
            el.appendChild(leftArrowBtn)

            carousel.addEventListener('scroll', updateArrows)
            carousel.style.cursor = 'grab'
            carousel.addEventListener('mousedown', onMouseDown)
            carousel.addEventListener('mouseleave', onMouseLeave)
            carousel.addEventListener('mouseup', onMouseUp)
            carousel.addEventListener('mousemove', onMouseMove)
            carousel.addEventListener('touchstart', onTouchStart, {passive: false})
            carousel.addEventListener('touchend', onTouchEnd)
            carousel.addEventListener('touchmove', onTouchMove, {passive: false})
            updateArrows()
        }

        const removeArrows = () => {
            carousel.removeEventListener('scroll', updateArrows)
            carousel.removeEventListener('mousedown', onMouseDown)
            carousel.removeEventListener('mouseleave', onMouseLeave)
            carousel.removeEventListener('mouseup', onMouseUp)
            carousel.removeEventListener('mousemove', onMouseMove)
            carousel.removeEventListener('touchstart', onTouchStart)
            carousel.removeEventListener('touchend', onTouchEnd)
            carousel.removeEventListener('touchmove', onTouchMove)
            carousel.style.cursor = ''
            if (rightArrowBtn) {
                rightArrowBtn.removeEventListener('click', handleRightClick)
                rightArrowBtn.remove()
                rightArrowBtn = null
            }
            if (leftArrowBtn) {
                leftArrowBtn.removeEventListener('click', handleLeftClick)
                leftArrowBtn.remove()
                leftArrowBtn = null
            }
            el.style.position = ''
        }

        const expandItems = () => {
            const items = carousel.querySelectorAll(':scope > li')
            items.forEach((li) => {
                li.style.flex = '1 1 0%'
                const wrapper = li.querySelector('.shop-by-brand-image-wrapper-style1')
                if (wrapper) {
                    wrapper.style.width = '100%'
                    wrapper.style.height = 'auto'
                    wrapper.style.aspectRatio = '1 / 1'
                }
            })
        }

        const resetItems = () => {
            const items = carousel.querySelectorAll(':scope > li')
            items.forEach((li) => {
                li.style.flex = '0 0 auto'
                const wrapper = li.querySelector('.shop-by-brand-image-wrapper-style1')
                if (wrapper) {
                    wrapper.style.width = ''
                    wrapper.style.height = ''
                    wrapper.style.aspectRatio = ''
                }
            })
            carousel.style.paddingRight = ''
        }

        const applyLayout = () => {
            resetItems()
            if (checkOverflow()) {
                el.style.paddingRight = '0'
                carousel.style.paddingRight = pxToRem(10)
                if (!rightArrowBtn) createArrows()
                updateArrows()
            } else {
                el.style.paddingRight = pxToRem(10)
                removeArrows()
                expandItems()
            }
        }

        applyLayout()
        window.addEventListener('resize', applyLayout)
        return () => {
            window.removeEventListener('resize', applyLayout)
            removeArrows()
        }
    }, [slotResponse, hasContent])

    // Let columns always extend to fill available space (no max width cap).
    const columnMaxWidth = 'none'

    const {columns, needsScroll} = useMemo(
        () => distributeColumns(items, effectiveMaxColumns, itemsKey),
        [items, effectiveMaxColumns, itemsKey]
    )

    const updateGTMdataLayer = (l1Name, l2Name) => {
        if (typeof window !== 'undefined' && window?.dataLayer) {
            window.dataLayer.push({
                event: 'mainNavClick',
                valueL1: l1Name,
                valueL2: l2Name
            })
        }
    }

    const handleLinkClick = (categoryName) => {
        updateGTMdataLayer(parentCategoryName, categoryName)
        if (onClose) onClose()
    }

    return (
        <Flex
            {...baseStyle.popoverBodyInner}
            display="flex"
            flexDirection="row"
            flexWrap="nowrap"
            alignItems="flex-start"
            gap={pxToRem(24)}
            width="100%"
            maxWidth="100%"
            minWidth={0}
            minHeight={0}
            flex="1 1 auto"
            position="relative"
        >
            {/* Columns: flexible width so they share space and shrink when narrow or slot present */}
            <Flex
                {...baseStyle.menuContainer}
                gap={pxToRem(32)}
                minWidth={0}
                flex="1 1 0%"
                flexDirection="row"
                flexWrap="nowrap"
                justifyContent="flex-start"
                alignItems="flex-start"
                alignSelf="flex-start"
                overflow="visible"
                marginTop={0}
                paddingTop={0}
            >
                {columns.map((columnCategories, colIndex) => {
                    return (
                        <Box
                            key={colIndex}
                            flex="1 1 0%"
                            minWidth={0}
                            maxWidth={columnMaxWidth}
                            display="flex"
                            flexDirection="column"
                            alignItems="flex-start"
                            justifyContent="flex-start"
                            alignSelf="flex-start"
                            gap={pxToRem(40)}
                            paddingTop={0}
                            marginTop={0}
                        >
                            {columnCategories.map((category) => {
                                const subcategories = category?.[itemsKey] || []
                                const parsedColors = parseCategoryColorConfig(
                                    category?.c_categoryTextColor
                                )
                                const textColor = parsedColors?.textColor ?? MENU_DEFAULT_TEXT_COLOR
                                const hoverColor =
                                    parsedColors?.hoverColor ?? MENU_DEFAULT_HOVER_COLOR

                                return (
                                    <Box
                                        key={category.id}
                                        data-testid={`mega-menu-column-${category?.id}`}
                                        paddingTop={0}
                                        marginTop={0}
                                        fontSize="1rem"
                                        lineHeight="normal"
                                    >
                                        <Link
                                            href={categoryUrlBuilder(category)}
                                            onClick={() => handleLinkClick(category?.name)}
                                            display="inline-flex"
                                            alignItems="center"
                                            fontFamily={fonts.primary}
                                            fontSize={pxToRem(16)}
                                            fontWeight="600"
                                            lineHeight="normal"
                                            letterSpacing={pxToRem(1)}
                                            color={textColor}
                                            textDecoration="none"
                                            marginBottom={
                                                subcategories.length > 0 ? pxToRem(15) : 0
                                            }
                                            _hover={{
                                                textDecoration: 'underline',
                                                color: hoverColor
                                            }}
                                            _focus={{
                                                outline: `${pxToRem(2)} solid ${hoverColor}`,
                                                outlineOffset: pxToRem(2),
                                                textDecoration: 'underline',
                                                color: hoverColor
                                            }}
                                            _focusVisible={{
                                                outline: `${pxToRem(2)} solid ${hoverColor}`,
                                                outlineOffset: pxToRem(2),
                                                textDecoration: 'underline',
                                                color: hoverColor
                                            }}
                                        >
                                            {category?.c_categoryIcon &&
                                                (category?.c_categoryIconPosition || 'left') ===
                                                    'left' && (
                                                    <Box
                                                        as="span"
                                                        marginRight={pxToRem(8)}
                                                        display="inline-flex"
                                                        alignItems="center"
                                                        dangerouslySetInnerHTML={{
                                                            __html: category.c_categoryIcon
                                                        }}
                                                    />
                                                )}
                                            <Box as="span">{category?.name}</Box>
                                            {category?.c_categoryIcon &&
                                                (category?.c_categoryIconPosition || 'left') ===
                                                    'right' && (
                                                    <Box
                                                        as="span"
                                                        marginLeft={pxToRem(8)}
                                                        display="inline-flex"
                                                        alignItems="center"
                                                        dangerouslySetInnerHTML={{
                                                            __html: category.c_categoryIcon
                                                        }}
                                                    />
                                                )}
                                        </Link>
                                        {subcategories.length > 0 && (
                                            <List
                                                spacing={pxToRem(10)}
                                                styleType="none"
                                                margin={0}
                                                padding={0}
                                            >
                                                {subcategories.map((subcategory) => {
                                                    const l3Parsed = parseCategoryColorConfig(
                                                        subcategory?.c_categoryTextColor
                                                    )
                                                    const l3TextColor =
                                                        l3Parsed?.textColor ??
                                                        MENU_DEFAULT_TEXT_COLOR
                                                    const l3HoverColor =
                                                        l3Parsed?.hoverColor ??
                                                        MENU_DEFAULT_HOVER_COLOR
                                                    return (
                                                        <ListItem
                                                            key={subcategory.id}
                                                            listStyleType="none"
                                                        >
                                                            <Link
                                                                href={categoryUrlBuilder(
                                                                    subcategory
                                                                )}
                                                                onClick={() =>
                                                                    handleLinkClick(
                                                                        subcategory?.name
                                                                    )
                                                                }
                                                                display="inline-flex"
                                                                alignItems="center"
                                                                fontFamily={fonts.primary}
                                                                fontSize={pxToRem(16)}
                                                                fontWeight="400"
                                                                lineHeight="normal"
                                                                letterSpacing={pxToRem(1)}
                                                                color={l3TextColor}
                                                                textDecoration="none"
                                                                padding={0}
                                                                _hover={{
                                                                    textDecoration: 'underline',
                                                                    color: l3HoverColor
                                                                }}
                                                                _focus={{
                                                                    outline: `${pxToRem(
                                                                        2
                                                                    )} solid ${l3HoverColor}`,
                                                                    outlineOffset: pxToRem(2),
                                                                    textDecoration: 'underline',
                                                                    color: l3HoverColor
                                                                }}
                                                                _focusVisible={{
                                                                    outline: `${pxToRem(
                                                                        2
                                                                    )} solid ${l3HoverColor}`,
                                                                    outlineOffset: pxToRem(2),
                                                                    textDecoration: 'underline',
                                                                    color: l3HoverColor
                                                                }}
                                                                data-testid={`mega-menu-link-${subcategory?.id}`}
                                                            >
                                                                {subcategory?.c_categoryIcon &&
                                                                    (subcategory?.c_categoryIconPosition ||
                                                                        'left') === 'left' && (
                                                                        <Box
                                                                            as="span"
                                                                            marginRight={pxToRem(8)}
                                                                            display="inline-flex"
                                                                            alignItems="center"
                                                                            dangerouslySetInnerHTML={{
                                                                                __html: subcategory.c_categoryIcon
                                                                            }}
                                                                        />
                                                                    )}
                                                                {subcategory?.name}
                                                                {subcategory?.c_categoryIcon &&
                                                                    (subcategory?.c_categoryIconPosition ||
                                                                        'left') === 'right' && (
                                                                        <Box
                                                                            as="span"
                                                                            marginLeft={pxToRem(8)}
                                                                            display="inline-flex"
                                                                            alignItems="center"
                                                                            dangerouslySetInnerHTML={{
                                                                                __html: subcategory.c_categoryIcon
                                                                            }}
                                                                        />
                                                                    )}
                                                            </Link>
                                                        </ListItem>
                                                    )
                                                })}
                                            </List>
                                        )}
                                    </Box>
                                )
                            })}
                        </Box>
                    )
                })}
            </Flex>
            {/* Content Asset Area */}
            {hasContent && (
                <Box
                    {...baseStyle.imageContainer}
                    {...(contentCount <= 1
                        ? {
                              flex: '0 0 auto',
                              width: 'auto',
                              maxWidth: 'none'
                          }
                        : {
                              flex: '0 0 auto',
                              width: {md: '30%', lg: '30%', xl: '42%'},
                              maxWidth: {md: '30%', lg: '30%', xl: '42%'}
                          })}
                    alignSelf="flex-start"
                    marginTop={0}
                    paddingTop={0}
                    ref={contentRef}
                >
                    <Box
                        sx={megaMenuContentSlotStyles}
                        minWidth={0}
                        padding={0}
                        dangerouslySetInnerHTML={{__html: slotResponse}}
                    />
                </Box>
            )}
        </Flex>
    )
}

MegaMenuContent.propTypes = {
    item: PropTypes.object,
    itemsKey: PropTypes.string,
    maxColumns: PropTypes.number,
    onClose: PropTypes.func,
    initialFocusRef: PropTypes.object
}

export {MegaMenuContent}
export default MegaMenuContent
